import React, { useState } from 'react';
import { formatDate, formatTime } from '../services/api';
import { config } from '../config';

// URL base para archivos subidos
const getUploadsUrl = () => {
  return config.apiUrl.replace('/api', '') || 'http://localhost:3001';
};

const NotasList = ({
  notas,
  onEliminar,
  usuarioActual,
  expandable = false,
  previewLimit = 5,
  collapsedChars = 220,
}) => {
  const [mostrarTodas, setMostrarTodas] = useState(false);
  const [notasExpandida, setNotasExpandida] = useState(() => new Set());

  if (!notas || notas.length === 0) {
    return (
      <div className="empty-state-modern">
        <i className="fas fa-sticky-note empty-icon"></i>
        <h3>No hay notas registradas</h3>
        <p>Las notas que agregues aparecerán aquí</p>
      </div>
    );
  }

  const notasVisibles =
    !expandable || mostrarTodas ? notas : notas.slice(0, previewLimit);

  const toggleNotaExpandida = (notaId) => {
    setNotasExpandida((prev) => {
      const next = new Set(prev);
      if (next.has(notaId)) next.delete(notaId);
      else next.add(notaId);
      return next;
    });
  };

  return (
    <div className="notas-container">
      {notasVisibles.map((nota) => {
        const puedeEliminar = usuarioActual?.id === nota.autor?.id || 
                             ['JEFE', 'ADMIN'].includes(usuarioActual?.rol);
        const fechaFormateada = formatDate(nota.createdAt);
        const horaFormateada = formatTime(nota.createdAt);
        const contenido = String(nota.contenido || '');
        const larga = contenido.length > collapsedChars;
        const expandida = notasExpandida.has(nota.id);
        const contenidoVisible =
          !larga || expandida ? contenido : `${contenido.slice(0, collapsedChars).trimEnd()}...`;

        return (
          <div key={nota.id} className="nota-card">
            <div className="nota-card-header">
              <div className="nota-card-author">
                <i className="fas fa-user-circle"></i>
                <div>
                  <strong>{nota.autor?.nombre || 'Usuario'}</strong>
                  <span className="nota-card-rol">{nota.autor?.rol || ''}</span>
                </div>
              </div>
              <div className="nota-card-meta">
                <span className="nota-card-fecha">
                  <i className="fas fa-calendar-alt"></i>
                  {fechaFormateada} - {horaFormateada}
                </span>
                {puedeEliminar && (
                  <button
                    className="btn-icon btn-danger"
                    onClick={() => onEliminar(nota.id)}
                    title="Eliminar nota"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                )}
              </div>
            </div>

            <div className="nota-card-body">
              <div className="nota-card-contenido" style={{ whiteSpace: 'pre-wrap' }}>
                {contenidoVisible}
              </div>
              {larga && (
                <button
                  type="button"
                  className="btn btn-link btn-sm nota-ver-mas"
                  onClick={() => toggleNotaExpandida(nota.id)}
                >
                  {expandida ? 'Ver menos' : 'Ver más'}
                </button>
              )}

              {nota.archivoAdjunto && (
                <div className="nota-card-archivo">
                  {(() => {
                    // Intentar parsear archivos como JSON (múltiples archivos)
                    let archivos = [];
                    try {
                      archivos = JSON.parse(nota.archivoAdjunto);
                    } catch (e) {
                      // Si falla, tratar como string único (compatibilidad con notas antiguas)
                      archivos = [{ nombre: 'Archivo adjunto', ruta: nota.archivoAdjunto }];
                    }

                    // Si es array, mostrar múltiples botones
                    if (Array.isArray(archivos) && archivos.length > 0) {
                      return (
                        <div style={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: '8px', 
                          marginTop: '12px' 
                        }}>
                          {archivos.map((archivo, index) => (
                            <a
                              key={index}
                              href={`${getUploadsUrl()}/uploads/${archivo.ruta || archivo}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-archivo-link"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 10px',
                                background: '#007bff',
                                color: 'white',
                                borderRadius: '4px',
                                textDecoration: 'none',
                                fontWeight: 500,
                                fontSize: '13px'
                              }}
                            >
                              <i className="fas fa-file"></i>
                              {archivo.nombre || 'Archivo'}
                            </a>
                          ))}
                        </div>
                      );
                    }

                    // Fallback: un solo archivo
                    return (
                      <a
                        href={`${getUploadsUrl()}/uploads/${nota.archivoAdjunto}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-archivo-link"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          background: '#007bff',
                          color: 'white',
                          borderRadius: '4px',
                          textDecoration: 'none',
                          fontWeight: 600,
                          marginTop: '12px'
                        }}
                      >
                        <i className="fas fa-paperclip"></i>
                        Ver archivo adjunto
                      </a>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        );
      })}
      {expandable && notas.length > previewLimit && (
        <div className="notas-lista-acciones">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setMostrarTodas((prev) => !prev)}
          >
            {mostrarTodas
              ? `Mostrar menos`
              : `Mostrar más notas (${notas.length - previewLimit} adicionales)`}
          </button>
        </div>
      )}
    </div>
  );
};

export default NotasList;

